// Logique pure de l'Annuaire clients (BI-667) : filtres, groupement, anomalies, CSV.

export const NO_GROUP = "Sans groupe";
export const CSV_HEADER =
  "num_env;finess;libelle;groupe;serveur;responsables;emails;telephones;statut";
export const STATUT_ACTIF = "Actif";
export const STATUT_INACTIF = "Désactivé";
export const CONTACT_SEPARATOR = " | ";

const str = (v) => (v === null || v === undefined ? "" : String(v));

const collator = new Intl.Collator("fr", { numeric: true, sensitivity: "base" });

export const groupLabel = (row) => str(row && row.groupe).trim() || NO_GROUP;

// Garde defensive : ne garde que les objets valides
export const sanitizeRows = (data) =>
  (Array.isArray(data) ? data : []).filter(
    (r) => r && typeof r === "object" && str(r.num_env) !== ""
  );

export const contactsOf = (row) =>
  Array.isArray(row && row.contacts)
    ? row.contacts.filter((c) => c && typeof c === "object")
    : [];

// --- Anomalies -------------------------------------------------------------
export function computeAnomalies(rows) {
  const servers = {};
  const groups = {};
  rows.forEach((r) => {
    const code = str(r.num_env);
    (servers[code] = servers[code] || new Set());
    (groups[code] = groups[code] || new Set());
    if (str(r.serveur).trim() !== "") servers[code].add(str(r.serveur).trim());
    groups[code].add(groupLabel(r));
  });
  return { servers, groups };
}

export const anomaliesOf = (row, anomalies) => {
  const code = str(row.num_env);
  return {
    multiServer: !!anomalies.servers[code] && anomalies.servers[code].size > 1,
    multiGroup: !!anomalies.groups[code] && anomalies.groups[code].size > 1,
    noGroup: groupLabel(row) === NO_GROUP,
  };
};

// --- Filtres ---------------------------------------------------------------
const searchText = (row) =>
  [
    row.num_env,
    row.finess,
    row.libelle,
    row.serveur,
    row.groupe,
    ...contactsOf(row).flatMap((c) => [c.nom, c.email, c.telephone]),
  ]
    .map(str)
    .join("\n")
    .toLowerCase();

// Filtres combines en ET. includeInactive=false => seuls les actifs.
export function filterRows(
  rows,
  { search = "", group, server, includeInactive = false } = {}
) {
  const q = str(search).trim().toLowerCase();
  return rows.filter((r) => {
    if (!includeInactive && r.actif === false) return false;
    if (group && groupLabel(r) !== group) return false;
    if (server && str(r.serveur).trim() !== server) return false;
    if (q && !searchText(r).includes(q)) return false;
    return true;
  });
}

// --- Groupement ------------------------------------------------------------
export function groupRows(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const g = groupLabel(r);
    if (!map.has(g)) map.set(g, []);
    map.get(g).push(r);
  });
  return Array.from(map.entries())
    .map(([name, items]) => ({
      name,
      rows: items.slice().sort((a, b) => collator.compare(str(a.num_env), str(b.num_env))),
      envCount: items.length,
      serverCount: new Set(
        items.map((i) => str(i.serveur).trim()).filter((s) => s !== "")
      ).size,
    }))
    .sort((a, b) => {
      if (a.name === NO_GROUP) return 1;
      if (b.name === NO_GROUP) return -1;
      return collator.compare(a.name, b.name);
    });
}

// Lignes a plat dans l'ordre d'affichage
export const flattenGroups = (groups) => groups.flatMap((g) => g.rows);

// --- CSV -------------------------------------------------------------------
// Neutralise l'injection de formules (=, +, -, @) par un apostrophe initial.
export const neutralize = (value) => {
  const s = str(value);
  return /^[=+\-@\t\r]/.test(s) ? "'" + s : s;
};

// Echappement RFC 4180 (separateur ;)
export const escapeCsv = (value) => {
  const s = str(value);
  return /[;"\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

// Trim AVANT neutralisation (SEC-018)
const cell = (value) => escapeCsv(neutralize(str(value).trim()));

// Telephone international strict : exempte de neutralisation (colonne telephones seule)
const PHONE_RE = /^\+\d[\d .]*$/;
const neutralizePhone = (v) => (PHONE_RE.test(v) ? v : neutralize(v));

// Alignement : une valeur absente donne une chaine vide a sa position ;
// si tout est vide, la cellule est vide.
const joinParts = (parts, neutralizer = neutralize) => {
  const vals = parts.map((p) => str(p).trim());
  if (vals.every((p) => p === "")) return "";
  return vals.map(neutralizer).join(CONTACT_SEPARATOR);
};

export function contactColumns(row) {
  const contacts = contactsOf(row);
  return {
    responsables: joinParts(
      contacts.map((c) => {
        const nom = str(c.nom).trim();
        const role = str(c.role).trim();
        return nom && role ? `${nom} (${role})` : nom;
      })
    ),
    emails: joinParts(contacts.map((c) => c.email)),
    telephones: joinParts(
      contacts.map((c) => c.telephone),
      neutralizePhone
    ),
  };
}

// Genere le contenu CSV (avec BOM, CRLF) pour des lignes deja filtrees/ordonnees.
export function buildDirectoryCsv(rows) {
  const lines = [CSV_HEADER];
  rows.forEach((r) => {
    const c = contactColumns(r);
    // contacts deja neutralises par partie : on n'applique que l'echappement
    lines.push(
      [
        cell(r.num_env),
        cell(r.finess),
        cell(r.libelle),
        cell(groupLabel(r)),
        cell(r.serveur),
        escapeCsv(c.responsables),
        escapeCsv(c.emails),
        escapeCsv(c.telephones),
        cell(r.actif === false ? STATUT_INACTIF : STATUT_ACTIF),
      ].join(";")
    );
  });
  return "﻿" + lines.join("\r\n") + "\r\n";
}

// Slug strict pour le nom de fichier : [a-z0-9-], 30 car. max (BI-694)
export const slugify = (value) =>
  str(value)
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30)
    .replace(/-+$/g, "");

// Nom du fichier : rappel indicatif des filtres actifs (nom inchange sans filtre)
export function csvFilename(
  date = new Date(),
  { search = "", group, server, includeInactive = false } = {}
) {
  const p = (n) => String(n).padStart(2, "0");
  let name = `annuaire-clients-${date.getFullYear()}${p(date.getMonth() + 1)}${p(
    date.getDate()
  )}`;
  const add = (label, value) => {
    const slug = slugify(value);
    if (slug) name += `-${label}-${slug}`;
  };
  if (group) add("groupe", group);
  if (server) add("serveur", server);
  if (str(search).trim()) add("recherche", search);
  if (includeInactive) name += "-avec-desactives";
  return `${name}.csv`;
}
